import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Loading } from '../components/Loading';
import { CoverImage } from '../components/CoverImage';
import type { Novel, UserReadingProgress } from '../services/types';

// 使用語義化 badge 類別
const statusConfig: Record<string, { badge: string; label: string; dotColor: string }> = {
  pending: { badge: 'badge-neutral', label: '等待中', dotColor: 'bg-neutral-400' },
  crawling: { badge: 'badge-primary', label: '下載中', dotColor: 'bg-primary-500' },
  completed: { badge: 'badge-success', label: '已完成', dotColor: 'bg-success-500' },
  failed: { badge: 'badge-danger', label: '失敗', dotColor: 'bg-danger-500' },
  paused: { badge: 'badge-warning', label: '已暫停', dotColor: 'bg-warning-500' }
};

type SortOption = 'lastRead' | 'chapters' | 'name' | 'updated';
type FilterOption = 'all' | 'favorites' | 'reading' | 'completed' | 'pending';

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'lastRead', label: '最近閱讀' },
  { value: 'updated', label: '最近更新' },
  { value: 'chapters', label: '章節數' },
  { value: 'name', label: '名稱' }
];

const filterOptions: { value: FilterOption; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'favorites', label: '我的收藏' },
  { value: 'reading', label: '閱讀中' },
  { value: 'completed', label: '已完成' },
  { value: 'pending', label: '未開始' }
];

interface NovelCardProps {
  novel: Novel;
  userProgress?: UserReadingProgress | null;
  isFavorited?: boolean;
}

function NovelCard({ novel, userProgress, isFavorited }: NovelCardProps) {
  const config = statusConfig[novel.status];
  const progress = novel.total_chapters > 0
    ? Math.round((novel.crawled_chapters / novel.total_chapters) * 100)
    : 0;

  const hasReadProgress = !!userProgress;

  const formatLastRead = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return '今天';
    if (diffDays === 1) return '昨天';
    if (diffDays < 7) return `${diffDays} 天前`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} 週前`;
    return `${Math.floor(diffDays / 30)} 月前`;
  };

  return (
    <Link
      to={`/novels/${novel.id}`}
      className="card-interactive group overflow-hidden"
    >
      {/* 封面區域 */}
      <div className="relative aspect-[3/4] bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        <CoverImage
          src={novel.cover_url}
          alt={novel.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />

        {/* 狀態標籤 */}
        <div className="absolute top-2 right-2">
          <span className={`badge ${config.badge} backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80`}>
            <span className={`w-1.5 h-1.5 rounded-full ${config.dotColor} ${novel.status === 'crawling' ? 'animate-pulse' : ''}`} />
            {config.label}
          </span>
        </div>

        {/* 下載進度條 */}
        {novel.status === 'crawling' && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* 閱讀進度遮罩 */}
        {hasReadProgress && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
            <div className="flex items-center gap-2 text-white/90 text-xs">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{formatLastRead(userProgress?.updated_at)}</span>
            </div>
          </div>
        )}

        {/* 收藏標記 */}
        {isFavorited && (
          <div className="absolute top-2 left-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-danger-500 text-white shadow-sm">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </span>
          </div>
        )}
      </div>

      {/* 資訊區域 */}
      <div className="p-3">
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 line-clamp-1 group-hover:text-primary-500 transition-colors">
          {novel.title}
        </h3>

        {novel.author && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-1">
            {novel.author}
          </p>
        )}

        <div className="flex items-center justify-between mt-2 text-xs text-neutral-400 dark:text-neutral-500">
          <span>{novel.crawled_chapters} 章</span>
          {novel.status === 'completed' && (
            <span className="text-success-500 font-medium">全本</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function NovelListPage() {
  const { isAdmin } = useAuth();
  const [novels, setNovels] = useState<Novel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('lastRead');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [userProgressMap, setUserProgressMap] = useState<Record<string, UserReadingProgress>>({});
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadNovels();
  }, []);

  const loadNovels = async () => {
    try {
      const [data, progressList, favorites] = await Promise.all([
        api.getNovels(),
        api.getAllReadingProgress(),
        api.getFavorites()
      ]);
      setNovels(data);

      const progressMap: Record<string, UserReadingProgress> = {};
      progressList.forEach(p => {
        progressMap[p.novel_id] = p;
      });
      setUserProgressMap(progressMap);

      setFavoriteIds(new Set(favorites.map(f => f.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load novels');
    } finally {
      setLoading(false);
    }
  };

  // 取得篩選數量
  const getFilterCount = (value: FilterOption) => {
    switch (value) {
      case 'favorites': return novels.filter(n => favoriteIds.has(n.id)).length;
      case 'reading': return novels.filter(n => userProgressMap[n.id] && n.status !== 'completed').length;
      case 'completed': return novels.filter(n => n.status === 'completed').length;
      case 'pending': return novels.filter(n => !userProgressMap[n.id] && n.status === 'completed').length;
      default: return novels.length;
    }
  };

  const filteredAndSortedNovels = useMemo(() => {
    let result = [...novels];

    switch (filterBy) {
      case 'favorites':
        result = result.filter(n => favoriteIds.has(n.id));
        break;
      case 'reading':
        result = result.filter(n => userProgressMap[n.id] && n.status !== 'completed');
        break;
      case 'completed':
        result = result.filter(n => n.status === 'completed');
        break;
      case 'pending':
        result = result.filter(n => !userProgressMap[n.id] && n.status === 'completed');
        break;
    }

    switch (sortBy) {
      case 'lastRead':
        result.sort((a, b) => {
          const aProgress = userProgressMap[a.id];
          const bProgress = userProgressMap[b.id];
          if (!aProgress && !bProgress) return 0;
          if (!aProgress) return 1;
          if (!bProgress) return -1;
          return new Date(bProgress.updated_at).getTime() - new Date(aProgress.updated_at).getTime();
        });
        break;
      case 'updated':
        result.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        break;
      case 'chapters':
        result.sort((a, b) => b.crawled_chapters - a.crawled_chapters);
        break;
      case 'name':
        result.sort((a, b) => a.title.localeCompare(b.title, 'zh-TW'));
        break;
    }

    return result;
  }, [novels, sortBy, filterBy, userProgressMap, favoriteIds]);

  if (loading) {
    return <Loading text="載入書架中..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 mb-4 rounded-full bg-danger-50 dark:bg-danger-900/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-danger-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <p className="text-danger-600 dark:text-danger-400 mb-4">{error}</p>
        <button onClick={loadNovels} className="btn btn-primary btn-md">
          重試
        </button>
      </div>
    );
  }

  if (novels.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-24 h-24 mb-6 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
          <svg className="w-12 h-12 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <h2 className="heading-section mb-2">書架是空的</h2>
        <p className="body-base mb-6">開始添加你喜歡的小說吧</p>
        {isAdmin ? (
          <Link to="/admin/add-novel" className="btn btn-primary btn-md">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            新增小說
          </Link>
        ) : (
          <p className="body-small">請稍後再來查看</p>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="heading-display">我的書架</h1>
              <p className="body-base mt-2">共 {novels.length} 本小說</p>
            </div>
            {isAdmin && (
              <Link to="/admin/add-novel" className="btn btn-primary btn-md">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                新增小說
              </Link>
            )}
          </div>
        </header>

        {/* 篩選和排序控制 */}
        <div className="card mb-8">
          <div className="card-body">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* 篩選標籤 */}
              <div className="flex-1">
                <label className="label mb-3">篩選</label>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFilterBy(option.value)}
                      className={`btn btn-sm ${
                        filterBy === option.value
                          ? 'btn-primary'
                          : 'btn-ghost'
                      }`}
                    >
                      {option.label}
                      {option.value !== 'all' && (
                        <span className={`text-xs ${filterBy === option.value ? 'opacity-80' : 'text-neutral-400'}`}>
                          {getFilterCount(option.value)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* 排序選擇 */}
              <div className="sm:w-48">
                <label className="label mb-3">排序</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="select"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* 篩選結果提示 */}
        {filteredAndSortedNovels.length === 0 && (
          <div className="card">
            <div className="card-body flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="body-base mb-4">沒有符合條件的小說</p>
              <button onClick={() => setFilterBy('all')} className="btn btn-ghost btn-sm text-primary-500">
                查看全部
              </button>
            </div>
          </div>
        )}

        {/* 小說網格 */}
        {filteredAndSortedNovels.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredAndSortedNovels.map((novel) => (
              <NovelCard
                key={novel.id}
                novel={novel}
                userProgress={userProgressMap[novel.id]}
                isFavorited={favoriteIds.has(novel.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
